import { Card, cn } from '@design-system';
import {
  EmailTab,
  LinkTab,
  InviteClientContext,
  PrefillAnswersSection,
  useInviteClientProvider,
  useInviteClientStore,
  type InviteClientProviderProps,
} from '@/features/invite-client';
import { useIsEdjPilotAdvisor } from '@client/shared/hooks/use-edj-pilot-advisor';

interface InviteClientCardContentProps {
  title?: string;
  description?: string;
}

function InviteClientCardContent({
  title = 'Invite your first client',
  description = 'Tip: Invite yourself first to see exactly what your clients will experience',
}: InviteClientCardContentProps) {
  const { showPrefillAnswers } = useInviteClientStore();

  return (
    <Card
      className={cn('gap-6 p-6', {
        'max-h-[90vh] overflow-hidden': showPrefillAnswers,
      })}
    >
      {showPrefillAnswers ? (
        <PrefillAnswersSection />
      ) : (
        <>
          <div className="space-y-1">
            <h3 className="text-xl font-semibold text-gray-950">{title}</h3>
            <p className="text-foreground text-base">{description}</p>
          </div>
          <EmailTab
            showHeader={false}
            showAdditionalSettings={false}
            showBulkMode={false}
          />
        </>
      )}
    </Card>
  );
}

export interface InviteClientCardProps extends InviteClientCardContentProps {
  onInviteSuccess?: InviteClientProviderProps['onInviteSuccess'];
  onClose?: InviteClientProviderProps['onClose'];
  prefillWithAdvisor?: boolean;
}

export function InviteClientCard({
  title,
  description,
  onInviteSuccess,
  onClose,
  prefillWithAdvisor,
}: InviteClientCardProps) {
  const storeValue = useInviteClientProvider({
    onInviteSuccess,
    onClose,
    prefillWithAdvisor,
    skipEmailWarning: true,
  });

  return (
    <InviteClientContext.Provider value={storeValue}>
      <InviteClientCardContent
        title={title}
        description={description}
      />
    </InviteClientContext.Provider>
  );
}

export interface InviteLinkCardProps extends InviteClientCardContentProps {
  onClose?: InviteClientProviderProps['onClose'];
}

export function InviteLinkCard({
  title = 'Invite a client',
  description = 'Get a link to open yourself in a new tab or share it with your client via email.',
  onClose,
}: InviteLinkCardProps) {
  const storeValue = useInviteClientProvider({
    onClose,
    skipEmailWarning: true,
  });
  const isEdjPilot = useIsEdjPilotAdvisor();
  const descriptionForEdj =
    'Get a link to try on yourself in a new tab or share it with your client via your Edward Jones email address';

  return (
    <InviteClientContext.Provider value={storeValue}>
      <Card className="gap-6 p-6 shadow-none">
        <div className="space-y-1">
          <h3 className="text-xl font-semibold text-gray-950">{title}</h3>
          <p className="text-foreground text-base">
            {isEdjPilot ? descriptionForEdj : description}
          </p>
        </div>
        <LinkTab
          withHeader={false}
          showAdditionalSettings={false}
        />
      </Card>
    </InviteClientContext.Provider>
  );
}
